import firebase from 'firebase/compat/app';
const { FirebaseIDB } = globalThis;
import { getOfflinePathsForJob } from './MobilePaths.js';
import { Parallel } from '../../../modules/Parallel.js';
import { hasUnsavedChangesForJob, listenToUnsavedChangesForGroup } from './MobileUpdateManager.js';
import { getQueueRecordsForJob } from '../../../elements/katapult-maps-mobile/PhotoUpload/UploadQueueStore.js';
import { removeFile, storeFile } from '../../../elements/katapult-maps-mobile/OPFSStore.js';

const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const REMOVE_INACTIVE_JOBS_INTERVAL_MS = HOUR_IN_MS;
// Warn users of pending removal when jobs have been inactive for 3 days.
export const WARN_INACTIVE_JOBS_AFTER_MS = DAY_IN_MS * 3;
// Remove jobs that have been inactive for 4 days.
const REMOVE_INACTIVE_JOBS_AFTER_MS = DAY_IN_MS * 4;

class UnsavedChangesError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UnsavedChangesError';
  }
}

// ----------------- Define types used within this module -----------------

/**
 * @typedef {Record<string, Object>} OfflinePathDirectory
 */

/**
 * @typedef {Object} OfflineJobCachingProgress
 * @property {number} progress - The percentage of job data that has been successfully cached.
 * @property {boolean} [complete] - Whether the job caching is complete.
 * @property {boolean} [failed] - Whether the job has failed to cache.
 */

/**
 * @typedef {Object} CachedJobDetails
 * @property {string} jobId - The id of the offline job.
 * @property {string} name - The name of the offline job.
 * @property {number} addedAt - The timestamp of when the job was added.
 * @property {number} lastCachedAt - The timestamp of when the job data was last downloaded from the server.
 * @property {number} outOfSyncAt - The timestamp of when the job stopped being in sync with the server (or would not be determined to be in sync when offline).
 * @property {number} lastActiveAt - The timestamp of when the job was last active.
 * @property {boolean} hasServerChanges - Whether the server has new changes available to pull.
 * @property {boolean} syncedWithServer - Whether the cached data matches the latest server data.
 * @property {boolean} synced - Alias for syncedWithServer.
 * @property {number} serverLastModified - The timestamp of when the job was last modified on the server.
 * @property {string[]} paths - An array of the paths that have been cached for this job.
 * @property {OfflineJobCachingProgress} [cachingProgress] - An object representing the progress of caching this job for offline use (only included when sending offline job details in a message to the main thread).
 */

// ----------------- Set up global variables for this module -----------------

// The path in IndexedDB where the offline path directory is stored
const OFFLINE_PATH_DIRECTORY_PATH = '$offlinePathDirectory';

const AUTOMATICALLY_CACHE_JOB_DATA_IN_BACKGROUND = false;

// The name of the database the user is connected to
let _databaseName;

let _activeCompanyId;

let _isOnline = false;

let _isAuthenticated = false;

let _jobServerChangesLookup = {};

let _jobCachingProgress = {};

let _jobUnsavedChangesListeners = {};

let _jobServerLastModifiedListeners = {};

let _offlineJobsListeners = {};

let _activeJobCacheRequest = null;
let _jobCacheRequests = [];

/** @type {?string} */
let _currentJobId = null;

let _inactiveJobsInterval = null;

// ----------------- Define public facing functions for module -----------------

/**
 * Registers a path as being used by a job in the offline path directory.
 * @param {string} jobId - The id of the job using this path.
 * @param {string} path - The path being used by this job.
 */
export async function registerPathInOfflinePathDirectory(jobId, path) {
  // Build a period-delimited version of the path to use as a key under /paths in the offline path directory.
  const offlinePathDirectoryPathId = path.replaceAll('/', '.');
  // Set the path to true to indicate that it has been stored offline for this job.
  // This will be used to determine paths to clear when purging data for this job.
  await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/paths/${offlinePathDirectoryPathId}`, true);
}

/**
 * Marks a job as out of sync or not out of sync and notifies listeners accordingly.
 * @param {string} jobId
 * @param {boolean} outOfSync
 */
export async function notifyJobOutOfSync(jobId, outOfSync) {
  await setJobOutOfSync(jobId, outOfSync);
  await notifyOfflineJobsChange();
}

/**
 * Marks a job as having or not having server changes and notifies listeners accordingly.
 * @param {string} jobId
 * @param {boolean} hasServerChanges
 */
async function setJobHasServerChanges(jobId, hasServerChanges) {
  const cachedJob = await getCachedJobDetails(jobId);
  if (!cachedJob) throw new Error(`Cached job not found for jobId: ${jobId}`);

  // See if the server changes state for this job has changed.
  const hasChanged = _jobServerChangesLookup[jobId] !== hasServerChanges;
  if (!hasChanged) return;

  // Update the server changes lookup.
  _jobServerChangesLookup[jobId] = hasServerChanges;

  await setJobOutOfSync(jobId, hasServerChanges);
}

/**
 * Marks a job as out of sync or not out of sync and notifies listeners accordingly.
 * @param {string} jobId
 * @param {boolean} outOfSync
 */
async function setJobOutOfSync(jobId, outOfSync) {
  const cachedJob = await getCachedJobDetails(jobId);
  if (!cachedJob) throw new Error(`Cached job not found for jobId: ${jobId}`);

  if (outOfSync) {
    if (cachedJob.outOfSyncAt == null) {
      await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/out_of_sync_at`, Date.now());
    }
  } else {
    if (cachedJob.outOfSyncAt != null) {
      await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/out_of_sync_at`, null);
    }
  }
}

export async function setCurrentJobId(jobId) {
  _currentJobId = jobId;
  if (!_currentJobId) return;

  // If the job has already been added, update its last active timestamp.
  const cachedJob = await getCachedJobDetails(_currentJobId);
  if (!cachedJob) return;

  await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/last_active_at`, Date.now());

  notifyOfflineJobsChange();
}

export function setOnlineState(isOnline) {
  _isOnline = isOnline;

  listCachedJobs().then(async (jobs) => {
    if (_isOnline) {
      // When coming back online, check if any inactive jobs have updates available on the server.
      jobs.forEach((job) => {
        const isCurrentJob = _currentJobId === job.jobId;
        if (isCurrentJob) return;
        promptJobToBeCachedInBackground(job.jobId);
      });
    } else {
      // When going offline, record the out-of-sync timestamp for any jobs
      // that don't already have one.
      for (const job of jobs) {
        setJobOutOfSync(job.jobId, true);
      }
    }

    notifyOfflineJobsChange();
  });
}

export function setAuthenticatedState(isAuthenticated) {
  _isAuthenticated = isAuthenticated;
}

/**
 * Initializes the module.
 * Must be called before using any other functions in this module.
 * @param {string} databaseName
 * @param {string} activeCompanyId
 * @throws {Error} If FirebaseIDB has not been initialized.
 * @throws {Error} If no database name is provided.
 */
export async function init(databaseName, activeCompanyId) {
  if (!FirebaseIDB.initialized) throw new Error('FirebaseIDB must be initialized before initializing CachedJobManager');
  if (!databaseName) throw new Error('Database name is required to initialize CachedJobManager');

  _databaseName = databaseName;

  await setActiveCompanyId(activeCompanyId);

  await notifyOfflineJobsChange();

  // Remove inactive jobs immediately on initialization.
  removeInactiveJobs();
  // Clear any existing interval for removing inactive jobs.
  if (_inactiveJobsInterval) clearInterval(_inactiveJobsInterval);
  // Set up an interval to remove inactive jobs every hour.
  _inactiveJobsInterval = setInterval(removeInactiveJobs, REMOVE_INACTIVE_JOBS_INTERVAL_MS);
}

async function setActiveCompanyId(activeCompanyId) {
  if (!activeCompanyId) throw new Error('Active company id is required to initialize CachedJobManager');
  _activeCompanyId = activeCompanyId;

  // Update listeners for all cached jobs since the company id has changed.
  const jobs = await listCachedJobs();
  jobs.forEach((job) => initListenersForJob(job.jobId));
}

/**
 *
 * @param {(cachedJobs: CachedJobDetails[]) => void} callback
 * @returns {() => void} A function to call to stop listening for changes to the list of offline jobs.
 */
export function listenToOfflineJobs(callback) {
  const listenerId = firebase.database().ref().push().key;
  _offlineJobsListeners[listenerId] = { callback };

  // Call the callback with the initial data.
  listCachedJobs().then((jobs) => {
    callback(jobs);
  });

  const disconnect = () => {
    delete _offlineJobsListeners[listenerId];
  };
  return disconnect;
}

/**
 * Adds a job to the list of offline jobs and prepares it for offline use.
 * @param {string} jobId
 * @returns {Promise<CachedJobDetails>} The details of the existing cached job or the newly cached job.
 * @throws {Error} If no job id is provided.
 */
export async function addOfflineJob(jobId) {
  // If the job is already cached for offline use, we don't need to do anything.
  const existingCachedJob = await getCachedJobDetails(jobId);
  if (existingCachedJob) return existingCachedJob;

  // Store when this job was added to the offline path directory.
  await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/added_at`, Date.now());

  // Ensure the persist flag is set for this job in the offline path directory to prevent automatic deletion of this job's data by the offline data management system.
  // TODO: (2/9/2026) Remove this if we stop automatically deleting offline job data for the current job when a new job is selected.
  await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/persist`, true);

  // Cache the job's data for offline use.
  const cachedJob = await cacheJob(jobId).catch(async (error) => {
    // If there was an error caching the job, clean up any partial data and re-throw the error.
    await purgeJob(jobId);
    throw error;
  });

  // If the job we are adding is the current job, update its last active timestamp.
  if (_currentJobId === jobId) {
    await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/last_active_at`, Date.now());
  }

  // Setup listeners for this job.
  initListenersForJob(jobId);

  return cachedJob;
}

/**
 * Determines whether a job has local unsaved data.
 * This includes unsaved database changes and queued photo uploads.
 * @param {string} jobId - The job id to evaluate.
 * @returns {Promise<boolean>} True when unsaved changes or queued uploads exist.
 */
async function checkIfJobHasUnsavedData(jobId) {
  // Check is the job has unsaved changes
  const currentJobHasUnsavedChanges = await hasUnsavedChangesForJob(jobId);

  // Check if the job has any photos queued to be uploaded
  const photosToUpload = await getQueueRecordsForJob(jobId);
  const currentJobHasUnsavedPhotos = photosToUpload.length != 0;

  return currentJobHasUnsavedChanges || currentJobHasUnsavedPhotos;
}

/**
 * Removes a job from the list of offline jobs.
 * @param {string} jobId
 */
export async function removeOfflineJob(jobId, options = {}) {
  // Check is the job has any unsaved changes or photos if the option is set to block clearing jobs with unsaved data
  const { blockIfUnsavedDataExists } = options;
  if (blockIfUnsavedDataExists) {
    const jobHasUnsavedData = await checkIfJobHasUnsavedData(jobId);
    if (jobHasUnsavedData) return;
  }

  // Purge the job's data and turn off unsaved changes and server last modified listeners
  await purgeJob(jobId);
  destroyListenersForJob(jobId);
}

/**
 * Clears all cached offline jobs except the currently active job and any jobs with unsaved data
 */
export async function removeAllOfflineJobs() {
  // Get the list of offline jobs
  const offlineJobs = await listCachedJobs();

  // Filter out the current active job
  const offlineJobsToClear = offlineJobs.filter((job) => job.jobId !== _currentJobId);

  // Clear each offline job and turn off listeners for each job's paths
  const clearJobPromises = offlineJobsToClear.map((job) => removeOfflineJob(job.jobId, { blockIfUnsavedDataExists: true }));
  await Promise.all(clearJobPromises);
}

// TODO: (2/10/26) This function should not be public.  It is only public because of how live listeners are handled in FirebaseWebWorker.js.
/**
 * Returns an array of paths that are only used by the specified job and can be safely deleted when purging this job's offline data.
 * @param {string} jobId
 * @returns {Promise<string[]>}
 */
export async function getOfflinePathsOnlyUsedByJob(jobId) {
  const cachedJob = await getCachedJobDetails(jobId);
  if (!cachedJob) return [];

  const otherCachedJobs = (await listCachedJobs()).filter((job) => job.jobId !== jobId);

  const pathsUsedByOtherJobs = new Set(otherCachedJobs.flatMap((job) => job.paths));

  const pathsOnlyUsedByThisJob = cachedJob.paths.filter((path) => !pathsUsedByOtherJobs.has(path));
  return pathsOnlyUsedByThisJob;
}

/**
 * Caches a job for offline use by fetching the job's data from Firebase and storing it in IndexedDB.
 * @param {string} jobId - The id of the job to cache for offline use.
 * @returns {Promise<CachedJobDetails>}
 * @throws {Error} If no job id is provided.
 * @throws {UnsavedChangesError} If there are unsaved changes for this job that would be lost by caching the job data.
 */
export async function cacheJob(jobId) {
  if (!jobId) throw new Error('Missing job id');

  // Check for unsaved changes before beginning to cache to avoid starting the caching process if we know it will fail due to unsaved changes.
  // We will also continue to check for unsaved changes throughout the caching process to avoid overwriting local changes with stale data from the server.
  const unsavedChanges = await hasUnsavedChangesForJob(jobId);
  if (unsavedChanges) throw new UnsavedChangesError();

  const authToken = await getAuthToken();

  // Cache the job name before notifying initial progress to ensure that the job name is available when the main thread receives the initial progress update.
  await updateJobNameInOfflinePathsDirectory(jobId, authToken);

  try {
    // Notify that we are beginning to cache the job.
    setJobCachingProgress(jobId, { progress: 0, failed: false });
    notifyOfflineJobsChange();

    await cacheOfflinePathsForJob(jobId, authToken);

    // Fetch the server's last modified timestamp for this job and store it in the offline path directory to be used for future cache expiration checks.
    const latestServerLastModified = await getServerJobLastModifiedTimestamp(jobId);
    await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/server_last_modified`, latestServerLastModified);

    await cacheLayersForJob(jobId, authToken);

    await setJobHasServerChanges(jobId, false);

    // Record when the job data was last downloaded.
    await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/last_cached_at`, Date.now());
    // Clear the out-of-sync timestamp since the job is now in sync.
    await setJobOutOfSync(jobId, false);

    // Notify that the job has been cached and is synced.
    setJobCachingProgress(jobId, { progress: 100, failed: false });
    notifyOfflineJobsChange();
  } catch (error) {
    setJobCachingProgress(jobId, { progress: 100, failed: true });
    notifyOfflineJobsChange();
    throw error;
  }

  const cachedJob = await getCachedJobDetails(jobId);
  if (!cachedJob) throw new Error('Failed to find job entry after caching it');

  return cachedJob;
}

// ----------------- Define internal functions for module -----------------

/**
 *
 * @param {string} jobId
 * @returns {Promise<CachedJobDetails|void>}
 */
async function purgeJob(jobId) {
  const details = await getCachedJobDetails(jobId);
  if (!details) return;

  // Remove any image overlays from OPFS that are associated with this job.
  await purgeLayerImageOverlaysForJob(jobId);

  await purgeOfflinePathsForJob(jobId);

  // Remove this job's entry from the offline path directory.
  await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}`, null);

  notifyOfflineJobsChange();

  return details;
}

/**
 * Remove all image overlays from OPFS that are associated with a job.
 * @param {string} jobId - The id of the job whose associated image overlays should be removed.
 */
async function purgeLayerImageOverlaysForJob(jobId) {
  // Get the layers from the IDB and filter down to overlay layers
  const layers = await FirebaseIDB.get(`${_databaseName}/photoheight/jobs/${jobId}/layers/list`);
  const layerEntriesWithImageOverlays = Object.entries(layers ?? {}).filter(([, layer]) => layer.type == 'Overlay Layer');
  if (layerEntriesWithImageOverlays.length == 0) return;

  // Remove the image overlays for all applicable layers from OPFS
  const layerIdsWithImageOverlays = layerEntriesWithImageOverlays.map(([layerId]) => layerId);
  await Parallel.forEach(
    layerIdsWithImageOverlays,
    async (layerId) => {
      try {
        await removeFile('image_overlays', `${jobId}-${layerId}`);
      } catch (error) {
        // Don't do anything if the file was not found, but throw the error if it was some other issue.
        if (error.name !== 'NotFoundError') throw error;
      }
    },
    8
  );
}

/**
 * Checks if the server has new changes for a job by comparing the locally stored server last modified timestamp with the latest one from the server.
 * @param {string} jobId
 * @returns {Promise<boolean>} Whether the server has new changes that should be pulled.
 */
async function checkIfJobHasServerChanges(jobId) {
  if (!jobId) throw new Error('Missing job id');

  const cachedJob = await getCachedJobDetails(jobId);
  const { serverLastModified: localServerLastModified } = cachedJob ?? {};

  // Fresh data will have a server last modified timestamp that matches the one in firestore.

  // If we don't have one, the data is stale.
  if (localServerLastModified == null) return true;

  // If the server last modified timestamp is greater than the one we have, the data is stale.
  const remoteServerLastModified = await getServerJobLastModifiedTimestamp(jobId);
  if (remoteServerLastModified == null || remoteServerLastModified > localServerLastModified) return true;

  return false;
}

/**
 * Request that a job be re-synced.  Will be added to the queue if:
 * - It isn't the active job
 * - It doesn't have unsaved changes
 * - It hasn't already been requested to be synced
 * - Its data has expired according to the server
 * @param {string} jobId
 * @returns {Promise<void>}
 */
async function promptJobToBeCachedInBackground(jobId) {
  if (!jobId) throw new Error('Missing job id');

  // Don't request that the job be synced if automatic caching is disabled.
  if (!AUTOMATICALLY_CACHE_JOB_DATA_IN_BACKGROUND) return;

  // Don't request that the job be synced if the user is not authenticated.
  if (!_isAuthenticated) return;

  // Ignore requests for the active job.
  const isCurrentJob = jobId === _currentJobId;
  if (isCurrentJob) return;

  // Ignore requests if the job has unsaved changes.
  // The job will be re-requested once the changes are saved.
  const hasUnsavedChanges = await hasUnsavedChangesForJob(jobId);
  if (hasUnsavedChanges) return;

  // Ignore duplicate requests.
  const alreadyRequested = _jobCacheRequests.includes(jobId);
  if (alreadyRequested) return;

  // Check if the server has new changes before adding it to the queue.
  const hasServerChanges = await checkIfJobHasServerChanges(jobId);
  // If there are no server changes, we don't need to cache it.
  if (!hasServerChanges) return;

  // Add the job to the queue and begin processing the queue.
  _jobCacheRequests.push(jobId);
  processJobCacheRequests();
}

function processJobCacheRequests() {
  // If there is already an active job cache request, return.
  // We will process the next request when the active request is complete to ensure that we are not caching multiple jobs at the same time.
  if (_activeJobCacheRequest) return;

  // Get the next job id from the queue.
  const nextJobId = _jobCacheRequests.shift();
  // If there are no more job ids in the queue, return.
  if (!nextJobId) return;

  // Cache the job and set it as the active job cache request.
  // When the cache job promise resolves, clear the active job cache request and process the next request in the queue.
  _activeJobCacheRequest = cacheJob(nextJobId)
    .catch((err) => {
      const isUnsavedChangesError = err instanceof UnsavedChangesError;
      if (!isUnsavedChangesError) throw err;
    })
    .finally(() => {
      _activeJobCacheRequest = null;
      processJobCacheRequests();
    });
}

/**
 * Initializes all listeners for a job.
 * @param {string} jobId
 */
function initListenersForJob(jobId) {
  initUnsavedChangesListenerForJob(jobId);
  initJobLastModifiedTimestampListener(jobId);
}

/**
 *
 * @param {string} jobId
 */
function destroyListenersForJob(jobId) {
  destroyUnsavedChangesListenerForJob(jobId);
  destroyJobLastModifiedTimestampListener(jobId);
}

/**
 * Sets up a listener to track the progress of unsaved changes for a job.
 * @param {string} jobId
 */
function initUnsavedChangesListenerForJob(jobId) {
  destroyUnsavedChangesListenerForJob(jobId);

  _jobUnsavedChangesListeners[jobId] = { disconnect: null, unsavedChanges: false };

  const disconnect = listenToUnsavedChangesForGroup(jobId, (progressGroup) => {
    const hadUnsavedChanges = _jobUnsavedChangesListeners[jobId].unsavedChanges;
    const unsavedChanges = !progressGroup.complete;
    _jobUnsavedChangesListeners[jobId].unsavedChanges = unsavedChanges;
    if (hadUnsavedChanges && !unsavedChanges) promptJobToBeCachedInBackground(jobId);
  });

  _jobUnsavedChangesListeners[jobId].disconnect = disconnect;
}

/**
 * Destroys the listener that tracks the progress of unsaved changes for a job.
 * @param {string} jobId
 */
function destroyUnsavedChangesListenerForJob(jobId) {
  const listener = _jobUnsavedChangesListeners[jobId];
  if (listener?.disconnect) listener.disconnect();
  delete _jobUnsavedChangesListeners[jobId];
}

/**
 * Sets up a listener to track the last modified timestamp for a job on the server.
 * @param {string} jobId
 */
function initJobLastModifiedTimestampListener(jobId) {
  destroyJobLastModifiedTimestampListener(jobId);

  const docRef = firebase.firestore().collection('jobs').doc(jobId);
  const disconnect = docRef.onSnapshot(async (doc) => {
    // Ensure the callback's listener config still exists.
    if (_jobServerLastModifiedListeners[jobId] == null) return;

    const data = doc.data();
    const lastUpdated = data?.metadata?.last_updated;
    if (lastUpdated) {
      const lastUpdatedMillis = lastUpdated.toMillis();
      _jobServerLastModifiedListeners[jobId].lastModified = lastUpdatedMillis;

      // If the active job is handled separately in FirebaseWebWorker.js, skip modifying it here.
      const isCurrentJob = jobId === _currentJobId;
      if (isCurrentJob) return;

      const hasServerChanges = await checkIfJobHasServerChanges(jobId);
      await setJobHasServerChanges(jobId, hasServerChanges);
      await notifyOfflineJobsChange();

      promptJobToBeCachedInBackground(jobId);
    }
  });

  _jobServerLastModifiedListeners[jobId] = { disconnect };
}

function destroyJobLastModifiedTimestampListener(jobId) {
  const listener = _jobServerLastModifiedListeners[jobId];
  if (listener?.disconnect) listener.disconnect();
  delete _jobServerLastModifiedListeners[jobId];
}

/**
 * Notifies listeners that the cached jobs have changed.
 */
export async function notifyOfflineJobsChange() {
  const jobs = await listCachedJobs();
  for (const listenerId in _offlineJobsListeners) {
    const listener = _offlineJobsListeners[listenerId];
    listener.callback(jobs);
  }
}

function getFirebaseUrlFromPath(path, authToken) {
  return `https://${_databaseName}.firebaseio.com/${path}.json?auth=${authToken}`;
}

/**
 * Gets the current user's auth token to be used for authenticating requests to Firebase.
 * @returns {Promise<string>}
 * @throws Will throw an error if there is no authenticated user or if there is an issue getting the auth token.
 */
async function getAuthToken() {
  const { currentUser } = firebase.auth();
  if (!currentUser) throw new Error('User not authenticated');
  const authToken = await currentUser.getIdToken();
  return authToken;
}

/**
 * Fetches the contents of the offline path directory from IndexedDB.
 * @returns {Promise<OfflinePathDirectory>}
 */
async function getOfflinePathDirectory() {
  const offlinePathDirectory = await FirebaseIDB.get(OFFLINE_PATH_DIRECTORY_PATH);
  return offlinePathDirectory ?? {};
}

/**
 *
 * @param {string} jobId
 * @param {object} [options]
 * @property {OfflinePathDirectory} [options.offlinePathDirectory] - The contents from the offline path directory (will be fetched if not provided).
 *
 * @returns {Promise<?CachedJobDetails>}
 */
async function getCachedJobDetails(jobId, options) {
  const offlinePathDirectory = options?.offlinePathDirectory ?? (await getOfflinePathDirectory());
  const jobOfflineData = offlinePathDirectory[jobId];
  if (!jobOfflineData) return null;
  const hasServerChanges = _jobServerChangesLookup[jobId] === true;
  const cachingProgress = _jobCachingProgress[jobId] ?? { complete: true, progress: 100 };
  const lastCachedAt = jobOfflineData.last_cached_at ?? null;
  const outOfSyncAt = jobOfflineData.out_of_sync_at ?? null;
  const syncedWithServer = outOfSyncAt == null;
  const details = {
    jobId,
    name: jobOfflineData.name,
    addedAt: jobOfflineData.added_at,
    lastCachedAt,
    outOfSyncAt,
    lastActiveAt: jobOfflineData.last_active_at,
    hasServerChanges,
    syncedWithServer,
    synced: syncedWithServer,
    serverLastModified: jobOfflineData.server_last_modified,
    paths: Object.keys(jobOfflineData.paths ?? {}).map((path) => path.replaceAll('.', '/')),
    cachingProgress
  };
  return details;
}

export async function listCachedJobs() {
  const offlinePathDirectory = await getOfflinePathDirectory();
  const jobIds = Object.keys(offlinePathDirectory).filter((key) => key !== 'default');
  const offlineJobDetails = await Promise.all(jobIds.map((jobId) => getCachedJobDetails(jobId, { offlinePathDirectory })));
  const filteredOfflineJobDetails = offlineJobDetails.filter((job) => job != null && job.name != null);
  return filteredOfflineJobDetails;
}

/**
 * Gets the last modified timestamp for a job.
 * @param {string} jobId
 * @returns {Promise<?number>}
 */
async function fetchServerJobLastModifiedTimestamp(jobId) {
  const docRef = firebase.firestore().collection('jobs').doc(jobId);
  const doc = await docRef.get();
  const data = doc.data();
  const lastUpdated = data?.metadata?.last_updated;
  if (!lastUpdated) return null;
  const lastUpdatedMillis = lastUpdated.toMillis();
  return lastUpdatedMillis;
}

/**
 * Gets the last modified timestamp for a job from the listener if it exists, or from the server if it doesn't.
 * @param {string} jobId
 * @returns {Promise<?number>}
 */
async function getServerJobLastModifiedTimestamp(jobId) {
  const timestampFromListener = _jobServerLastModifiedListeners[jobId]?.lastModified;
  if (timestampFromListener != null) return timestampFromListener;

  // If we don't have a listener for this job, fetch the last modified timestamp directly from the server.
  const timestampFromServer = await fetchServerJobLastModifiedTimestamp(jobId);
  return timestampFromServer;
}

/**
 * Updates the job name in the offline path directory for a job.
 * @param {string} jobId
 * @param {string} authToken
 * @returns {Promise<string>} The job name.
 */
async function updateJobNameInOfflinePathsDirectory(jobId, authToken) {
  const jobNameUrl = getFirebaseUrlFromPath(`photoheight/job_permissions/${_activeCompanyId}/jobs/${jobId}/name`, authToken);
  const jobName = await fetch(jobNameUrl, { method: 'GET' }).then((res) => res.json());
  await FirebaseIDB.set(`${OFFLINE_PATH_DIRECTORY_PATH}/${jobId}/name`, jobName);
  return jobName;
}

async function cacheOfflinePathsForJob(jobId, authToken) {
  if (!jobId) throw new Error('Job id is required to cache offline job data.');
  if (!authToken) throw new Error('Auth token is required to cache offline job data.');

  // Check for unsaved changes before beginning to cache to avoid starting the caching process if we know it will fail due to unsaved changes.
  // We will also continue to check for unsaved changes throughout the caching process to avoid overwriting local changes with stale data from the server.
  const unsavedChanges = await hasUnsavedChangesForJob(jobId);
  if (unsavedChanges) throw new UnsavedChangesError();

  // Populate the offline paths with the variable data
  const offlineJobPaths = await getOfflinePathsForJob(jobId);

  // Fetch each offline path and store its data in indexed db.
  await Parallel.forEach(
    offlineJobPaths,
    async (path) => {
      // Fetch the data for this path.
      const pathUrl = getFirebaseUrlFromPath(path, authToken);
      const data = await fetch(pathUrl, { method: 'GET' }).then((res) => res.json());
      // Check again for unsaved changes before storing the data.
      const unsavedChanges = await hasUnsavedChangesForJob(jobId);
      if (unsavedChanges) throw new UnsavedChangesError();
      // Store the data in indexed db for local use.
      const databasePath = `${_databaseName}/${path}`;
      await FirebaseIDB.set(databasePath, data);
      await registerPathInOfflinePathDirectory(jobId, databasePath);
    },
    8
  );
}

async function purgeOfflinePathsForJob(jobId) {
  const pathsOnlyUsedByThisJob = await getOfflinePathsOnlyUsedByJob(jobId);

  // WARNING AND TODO: This makes a large assumption that all paths are represented in indexed db as object stores.
  // This seems to often be true, but there is no guarantee of this, so we should update FirebaseIDB.set()
  // to delete object stores that are set to null so we can call it here and handle the case where paths are
  // not represented as object stores in indexed db.
  const pathLookup = Object.fromEntries(pathsOnlyUsedByThisJob.map((x) => [x, true]));
  await FirebaseIDB.deleteObjectStores(pathLookup);
}

async function cacheLayersForJob(jobId, authToken) {
  if (!jobId) throw new Error('Job id is required to cache job layers.');
  if (!authToken) throw new Error('Auth token is required to cache job layers.');

  // Check for unsaved changes before beginning to cache to avoid starting the caching process if we know it will fail due to unsaved changes.
  // We will also continue to check for unsaved changes throughout the caching process to avoid overwriting local changes with stale data from the server.
  const unsavedChanges = await hasUnsavedChangesForJob(jobId);
  if (unsavedChanges) throw new UnsavedChangesError();

  // Fetch layers from Google Cloud Storage and store them in FirebaseIDB.
  const layersUrl = getFirebaseUrlFromPath(`photoheight/jobs/${jobId}/layers/list`, authToken);
  const layers = await fetch(layersUrl, { method: 'GET' }).then((res) => res.json());
  const layerEntries = Object.entries(layers ?? {});
  await Parallel.forEach(
    layerEntries,
    async ([layerId, layer]) => {
      // Get the layers file name from the layer data
      const layerIsImageOverlay = layer.type == 'Overlay Layer';
      const layerFileName = layerIsImageOverlay ? layerId : layer.storage_file_name;
      if (!layerFileName) return;

      // Get the layer url from google cloud storage
      const storageRef = firebase.storage().ref(`job_files/${jobId}/${layerFileName}`);
      const url = await storageRef.getDownloadURL();

      // Store the image data for the overlay in OPFS
      if (layer.type == 'Overlay Layer') {
        const imageBlob = await fetch(url).then((response) => response.blob());
        await storeFile('image_overlays', `${jobId}-${layerId}`, imageBlob);
      }
      // Store the layer in the IDB if it's not an overlay image
      else {
        // Get the layer from google cloud storage.
        const layerData = await fetch(url).then((response) => response.json());
        // Check for unsaved changes before storing the layer data to avoid overwriting local changes with stale data from the server.
        const unsavedChanges = await hasUnsavedChangesForJob(jobId);
        if (unsavedChanges) throw new UnsavedChangesError();
        // Store the layer in FirebaseIDB.
        const layerDataPath = `${_databaseName}/photoheight/jobs/${jobId}/layers/data/${layerId}`;
        await FirebaseIDB.set(layerDataPath, layerData);
      }
    },
    8
  );
}

function setJobCachingProgress(jobId, progress, options) {
  const cachingProgress = (_jobCachingProgress[jobId] ??= {});
  Object.assign(cachingProgress, progress);
  cachingProgress.complete = cachingProgress.progress === 100;
}

async function removeInactiveJobs() {
  const offlineJobs = await listCachedJobs();

  const inactiveAfterTime = Date.now() - REMOVE_INACTIVE_JOBS_AFTER_MS;

  const inactiveJobs = offlineJobs.filter((job) => {
    // Ensure the current job is not considered inactive.
    const isCurrentJob = job.jobId === _currentJobId;
    if (isCurrentJob) return false;
    // Consider a job inactive if it was last opened (or added if it was never opened) more than a certain amount of time ago.
    const jobIsInactive = (job.lastActiveAt ?? job.addedAt ?? 0) <= inactiveAfterTime;
    return jobIsInactive;
  });

  // Remove all inactive jobs.
  await Parallel.forEach(inactiveJobs, (job) => removeOfflineJob(job.jobId, { blockIfUnsavedDataExists: true }), 8);
}
